import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const OrderInspector = ({ selectedOrder, onOrderUpdate, onManualOverride }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedOrder, setEditedOrder] = useState(selectedOrder || {});

  if (!selectedOrder) {
    return (
      <div className="bg-card border border-border rounded-lg p-8 text-center">
        <Icon name="MousePointer" size={48} className="mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">Select an Order</h3>
        <p className="text-muted-foreground">
          Click on any order from the queue above to view detailed execution information
        </p>
      </div>
    );
  }

  const executionSteps = [
    {
      step: 'AI Recommendation',
      timestamp: selectedOrder?.aiTimestamp,
      status: 'completed',
      details: `AI recommended ${selectedOrder?.side} ${selectedOrder?.quantity} shares at ₹${selectedOrder?.targetPrice}`
    },
    {
      step: 'Order Validation',
      timestamp: selectedOrder?.validationTimestamp,
      status: 'completed',
      details: 'Risk checks passed, sufficient balance confirmed'
    },
    {
      step: 'Venue Routing',
      timestamp: selectedOrder?.routingTimestamp,
      status: 'completed',
      details: `Routed to ${selectedOrder?.venue?.toUpperCase()} for execution`
    },
    {
      step: 'Order Submission',
      timestamp: selectedOrder?.timestamp,
      status: selectedOrder?.status === 'pending' ? 'in_progress' : 'completed',
      details: `Order submitted to ${selectedOrder?.venue?.toUpperCase()} exchange`
    },
    {
      step: 'Execution',
      timestamp: selectedOrder?.executionTimestamp,
      status: selectedOrder?.status === 'completed' ? 'completed' : 
              selectedOrder?.status === 'failed' ? 'failed' : 'pending',
      details: selectedOrder?.status === 'completed' ? 
               `Executed ${selectedOrder?.filledQuantity || selectedOrder?.quantity} shares at ₹${selectedOrder?.executionPrice || selectedOrder?.targetPrice}` :
               selectedOrder?.status === 'failed' ? selectedOrder?.errorMessage : 'Awaiting execution'
    }
  ];

  const getStepIcon = (status) => {
    switch (status) {
      case 'completed': return 'CheckCircle';
      case 'in_progress': return 'Clock';
      case 'failed': return 'XCircle';
      default: return 'Circle';
    }
  };

  const getStepColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success';
      case 'in_progress': return 'text-primary';
      case 'failed': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const handleSaveChanges = () => {
    onOrderUpdate(editedOrder);
    setIsEditing(false);
  };

  const handleManualOverride = () => {
    onManualOverride(selectedOrder?.orderId);
  };

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-foreground">Order Details</h3>
            <p className="text-sm text-muted-foreground">Order ID: {selectedOrder?.orderId}</p>
          </div>
          <div className="flex items-center space-x-2">
            {!isEditing ? (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                  iconName="Edit"
                  iconSize={14}
                >
                  Edit
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleManualOverride}
                  iconName="Settings"
                  iconSize={14}
                >
                  Override
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditing(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleSaveChanges}
                  iconName="Save"
                  iconSize={14}
                >
                  Save
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="p-4 space-y-6">
        {/* Order Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Symbol
            </label>
            {isEditing ? (
              <Input
                type="text"
                value={editedOrder?.symbol}
                onChange={(e) => setEditedOrder({...editedOrder, symbol: e?.target?.value})}
                className="h-8"
              />
            ) : (
              <p className="text-sm font-medium text-foreground">{selectedOrder?.symbol}</p>
            )}
          </div>
          
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Quantity
            </label>
            {isEditing ? (
              <Input
                type="number"
                value={editedOrder?.quantity}
                onChange={(e) => setEditedOrder({...editedOrder, quantity: parseInt(e?.target?.value)})}
                className="h-8"
              />
            ) : (
              <p className="text-sm font-medium text-foreground">{selectedOrder?.quantity?.toLocaleString()}</p>
            )}
          </div>
          
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Target Price
            </label>
            {isEditing ? (
              <Input
                type="number"
                step="0.01"
                value={editedOrder?.targetPrice}
                onChange={(e) => setEditedOrder({...editedOrder, targetPrice: parseFloat(e?.target?.value)})}
                className="h-8"
              />
            ) : (
              <p className="text-sm font-medium text-foreground">
                ₹{selectedOrder?.targetPrice?.toLocaleString()}
              </p>
            )}
          </div>
          
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
              Status
            </label>
            <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
              selectedOrder?.status === 'completed' ? 'bg-success/10 text-success' :
              selectedOrder?.status === 'failed' ? 'bg-error/10 text-error' :
              selectedOrder?.status === 'executing'? 'bg-primary/10 text-primary' : 'bg-warning/10 text-warning'
            }`}>
              {selectedOrder?.status?.charAt(0)?.toUpperCase() + selectedOrder?.status?.slice(1)}
            </div>
          </div>
        </div>

        {/* Execution Timeline */}
        <div>
          <h4 className="text-sm font-semibold text-foreground mb-4">Execution Timeline</h4>
          <div className="space-y-4">
            {executionSteps?.map((step, index) => (
              <div key={index} className="flex items-start space-x-3">
                <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${getStepColor(step?.status)}`}>
                  <Icon name={getStepIcon(step?.status)} size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-foreground">{step?.step}</p>
                    {step?.timestamp && (
                      <p className="text-xs text-muted-foreground">
                        {new Date(step.timestamp)?.toLocaleTimeString()}
                      </p>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{step?.details}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Error Details */}
        {selectedOrder?.status === 'failed' && selectedOrder?.errorMessage && (
          <div className="bg-error/5 border border-error/20 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="AlertTriangle" size={20} className="text-error flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-semibold text-error mb-1">Execution Error</h4>
                <p className="text-sm text-error/80">{selectedOrder?.errorMessage}</p>
                {selectedOrder?.errorCode && (
                  <p className="text-xs text-error/60 mt-1">Error Code: {selectedOrder?.errorCode}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Performance Metrics */}
        {selectedOrder?.status === 'completed' && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4 bg-muted/30 rounded-lg">
            <div className="text-center">
              <p className="text-xs text-muted-foreground mb-1">Execution Time</p>
              <p className="text-sm font-medium text-foreground">
                {selectedOrder?.executionTime || '2.3s'}
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-muted-foreground mb-1">Slippage</p>
              <p className={`text-sm font-medium ${
                (selectedOrder?.slippage || 0) > 0 ? 'text-error' : 'text-success'
              }`}>
                {selectedOrder?.slippage || 0}%
              </p>
            </div>
            <div className="text-center">
              <p className="text-xs text-muted-foreground mb-1">Fill Rate</p>
              <p className="text-sm font-medium text-foreground">
                {((selectedOrder?.filledQuantity || selectedOrder?.quantity) / selectedOrder?.quantity * 100)?.toFixed(1)}%
              </p>
            </div>
          </div>
        )}

        {/* AI Decision Context */}
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
          <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center">
            <Icon name="Brain" size={16} className="mr-2 text-primary" />
            AI Decision Context
          </h4>
          <p className="text-sm text-muted-foreground">
            {selectedOrder?.aiReasoning || `AI recommended ${selectedOrder?.side} based on technical analysis showing strong ${selectedOrder?.side === 'buy' ? 'support' : 'resistance'} levels and favorable risk-reward ratio of 1:3.2`}
          </p>
          <div className="mt-2 flex items-center space-x-4 text-xs text-muted-foreground">
            <span>Confidence: {selectedOrder?.aiConfidence || '87%'}</span>
            <span>Model: {selectedOrder?.aiModel || 'ASTRA-v2.1'}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderInspector;